import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { DesignEditorSecondaryControls } from '../../../DesignEditor/components/DesignEditorControls';
import { CONTROL_SECTION, MAX_Z_INDEX } from '../../helpers';
import CardDesignerLogoControls from './components/CardDesignerLogoControls';
import CardDesignerLayoutControls from './components/CardDesignerLayoutControls';
import CardDesignerBackgroundControls from './components/CardDesignerBackgroundControls';
import CardDesignerForegroundControls from './components/CardDesignerForegroundControls';
import useComponentBounds from '../../../../../hooks/useComponentBounds';
import CardDesignerPhotoControls from './components/CardDesignerPhotoControls/CardDesignerPhotoControls';
import CardDesignerTextControls from './components/CardDesignerTextControls/CardDesignerTextControls';
import CardDesignerLayerControls from './components/CardDesignerLayerControls/CardDesignerLayerControls';
import CardDesignerGridControls from './components/CardDesignerGridControls';
import { FONT_FAMILY } from '../CardDesignerCardElements/CardDesignerCardElementText/helpers';

const propTypes = {
  className: PropTypes.string,
  activeControlSection: PropTypes.string.isRequired,
  onCardSettingsUpdate: PropTypes.func.isRequired,
  onCardElementCreate: PropTypes.func.isRequired,
  onCardElementSelect: PropTypes.func.isRequired,
  onCardElementUpdate: PropTypes.func.isRequired,
  onCardElementsUpdate: PropTypes.func.isRequired,
  onCardElementDelete: PropTypes.func.isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
  card: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  cardSide: PropTypes.object,
  cardRef: PropTypes.object,
  refetchCard: PropTypes.func,
  elements: PropTypes.array,
  selectedCardElement: PropTypes.object,
  logoElement: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }),
  backgroundElement: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }),
  foregroundElement: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }),
  printPartnerId: PropTypes.string,
  gridEnabled: PropTypes.bool,
  setGridEnabled: PropTypes.func,
  lastUsedFont: PropTypes.oneOf(Object.values(FONT_FAMILY)),
};

const CardDesignerSecondaryControlPanel = ({
  className,
  activeControlSection,
  onCardSettingsUpdate,
  onCardElementCreate,
  onCardElementSelect,
  onCardElementUpdate,
  onCardElementsUpdate,
  onCardElementDelete,
  onElementConfigurationChange,
  elements,
  selectedCardElement,
  logoElement,
  backgroundElement,
  foregroundElement,
  card,
  cardSide,
  cardRef,
  refetchCard,
  printPartnerId,
  gridEnabled,
  setGridEnabled,
  lastUsedFont,
}) => {
  const classes = classNames({
    [className]: !!className,
  });

  const { bounds } = useComponentBounds(cardRef);
  const { card_template: cardTemplate } = cardSide ?? {};

  const zIndexes = (
    card.card_side_groups?.records?.[0].card_sides?.records?.[0].card_template
      ?.card_elements ?? []
  )
    //   We do not want the logo z-index to be accounted here. So if found, skip it.
    .map(({ z_index }) => (z_index === MAX_Z_INDEX ? 0 : z_index ?? 0));

  return (
    <DesignEditorSecondaryControls className={classes}>
      {activeControlSection === CONTROL_SECTION.LAYOUT && (
        <CardDesignerLayoutControls
          onCardSettingsUpdate={onCardSettingsUpdate}
          card={card}
          refetchCard={refetchCard}
          printPartnerId={printPartnerId}
        />
      )}
      {activeControlSection === CONTROL_SECTION.LOGO && (
        <CardDesignerLogoControls
          element={logoElement}
          cardBounds={bounds}
          onCardElementCreate={onCardElementCreate}
          onCardElementUpdate={onCardElementUpdate}
          onCardElementDelete={onCardElementDelete}
          onElementConfigurationChange={onElementConfigurationChange}
        />
      )}
      {activeControlSection === CONTROL_SECTION.BACKGROUND &&
        backgroundElement && (
          <CardDesignerBackgroundControls
            element={backgroundElement}
            onElementConfigurationChange={onElementConfigurationChange}
          />
        )}
      {activeControlSection === CONTROL_SECTION.FOREGROUND &&
        backgroundElement && (
          <CardDesignerForegroundControls
            element={foregroundElement}
            onCardElementCreate={onCardElementCreate}
            onElementConfigurationChange={onElementConfigurationChange}
            zElementIndexMax={
              zIndexes.length > 0 ? Math.max(...zIndexes) + 1 : undefined
            }
          />
        )}
      {activeControlSection === CONTROL_SECTION.PHOTOS && (
        <CardDesignerPhotoControls
          cardPrintSize={
            cardTemplate?.print_size ?? cardTemplate?.print_product?.print_size
          }
          cardOrientation={cardTemplate?.orientation}
          onCardElementCreate={onCardElementCreate}
          zElementIndexMax={
            zIndexes.length > 0 ? Math.max(...zIndexes) + 1 : undefined
          }
        />
      )}
      {activeControlSection === CONTROL_SECTION.TEXTS && (
        <CardDesignerTextControls
          onCardElementCreate={onCardElementCreate}
          lastUsedFont={lastUsedFont}
          zElementIndexMax={
            zIndexes.length > 0 ? Math.max(...zIndexes) + 1 : undefined
          }
        />
      )}
      {activeControlSection === CONTROL_SECTION.LAYERS && (
        <CardDesignerLayerControls
          elements={elements}
          onCardElementUpdate={onCardElementUpdate}
          onCardElementsUpdate={onCardElementsUpdate}
          onCardElementSelect={onCardElementSelect}
          selectedCardElement={selectedCardElement}
        />
      )}
      {activeControlSection === CONTROL_SECTION.GRID && (
        <CardDesignerGridControls
          gridEnabled={gridEnabled}
          setGridEnabled={setGridEnabled}
        />
      )}
    </DesignEditorSecondaryControls>
  );
};

CardDesignerSecondaryControlPanel.propTypes = propTypes;

export default CardDesignerSecondaryControlPanel;
